import { useState } from 'react'
import { Toggle } from '@/components/ui/Toggle'
import { useUpdateReportSettings } from '@/lib/report-api'
import type { HoursReportClient } from '@/lib/report-types'

function parseRecipients(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

interface RecipientsForm {
  perProject: boolean
  globalText: string
  projectTexts: Record<string, string>
  weeklyEnabled: boolean
}

/**
 * Ontvangers gelden voor de hele klant, of per project. Welke modus actief is
 * volgt uit de data: zodra een project een eigen lijst heeft is het per project.
 */
function formOf(client: HoursReportClient): RecipientsForm {
  const soleProject = client.projects.length === 1 ? client.projects[0] : null
  const globalRecipients = soleProject?.recipientsSource === 'project' ? soleProject.recipients : client.recipients
  return {
    perProject: client.projects.length > 1 && client.projects.some((p) => p.recipientsSource === 'project'),
    globalText: globalRecipients.join(', '),
    projectTexts: Object.fromEntries(client.projects.map((p) => [p.projectId, p.recipients.join(', ')])),
    weeklyEnabled: client.weeklyEnabled,
  }
}

function sameForm(a: RecipientsForm, b: RecipientsForm): boolean {
  if (a.perProject !== b.perProject || a.weeklyEnabled !== b.weeklyEnabled) return false
  if (a.perProject) {
    const ids = new Set([...Object.keys(a.projectTexts), ...Object.keys(b.projectTexts)])
    return [...ids].every((id) => (a.projectTexts[id] ?? '').trim() === (b.projectTexts[id] ?? '').trim())
  }
  return a.globalText.trim() === b.globalText.trim()
}

export function ReportRecipientsSection({ client, canEdit }: { client: HoursReportClient; canEdit: boolean }) {
  const updateSettings = useUpdateReportSettings()
  const [saved, setSaved] = useState(() => formOf(client))
  const [form, setForm] = useState(saved)

  const multiProject = client.projects.length > 1
  const globalRecipients = parseRecipients(form.globalText)
  const projectRecipients = Object.fromEntries(
    client.projects.map((p) => [p.projectId, parseRecipients(form.projectTexts[p.projectId] ?? '')]),
  )
  const hasRecipients = form.perProject
    ? client.projects.some((p) => projectRecipients[p.projectId].length > 0)
    : globalRecipients.length > 0
  const dirty = !sameForm(form, saved)

  function switchMode(sameForAll: boolean) {
    setForm((current) => ({
      ...current,
      perProject: !sameForAll,
      projectTexts: sameForAll
        ? current.projectTexts
        : Object.fromEntries(
            client.projects.map((p) => [
              p.projectId,
              (current.projectTexts[p.projectId] ?? '').trim().length > 0 ? current.projectTexts[p.projectId] : current.globalText,
            ]),
          ),
    }))
  }

  function save() {
    const submitted = form
    updateSettings.mutate(
      {
        clientId: client.clientId,
        input: {
          recipients: form.perProject ? [] : globalRecipients,
          weeklyEnabled: hasRecipients ? form.weeklyEnabled : false,
          projectRecipients: Object.fromEntries(
            client.projects.map((p) => [p.projectId, form.perProject ? projectRecipients[p.projectId] : null]),
          ),
        },
      },
      {
        onSuccess: (overview) => {
          const fresh = overview.clients.find((c) => c.clientId === client.clientId)
          if (!fresh) return
          const next = formOf(fresh)
          setSaved(next)
          setForm((current) => (sameForm(current, submitted) ? next : current))
        },
      },
    )
  }

  return (
    <section className="space-y-3 rounded-lg border border-hairline p-3">
      {multiProject ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-ink">Zelfde ontvangers voor alle projecten</p>
            <p className="text-xs text-ink-faint">
              {form.perProject
                ? 'Elk project heeft een eigen lijst ontvangers.'
                : `Deze adressen krijgen het rapport van alle ${client.projects.length} projecten.`}
            </p>
          </div>
          <Toggle
            checked={!form.perProject}
            onChange={switchMode}
            label="Zelfde ontvangers voor alle projecten"
            disabled={!canEdit}
          />
        </div>
      ) : null}

      {form.perProject ? (
        <div className="space-y-2">
          <span className="label-caps block text-ink-faint">Ontvangers per project</span>
          <div className="divide-y divide-hairline">
            {client.projects.map((project) => (
              <div key={project.projectId} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-start gap-3 py-2">
                <span className="truncate pt-2 text-sm text-ink" title={project.projectName}>
                  {project.projectName}
                </span>
                <div>
                  <input
                    value={form.projectTexts[project.projectId] ?? ''}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        projectTexts: { ...current.projectTexts, [project.projectId]: e.target.value },
                      }))
                    }
                    className="field"
                    placeholder="naam@klant.nl, tweede@klant.nl"
                    aria-label={`Ontvangers van ${project.projectName}`}
                    readOnly={!canEdit}
                  />
                  {projectRecipients[project.projectId].length === 0 ? (
                    <span className="mt-1 block text-xs text-status-approval">Geen ontvangers: dit project wordt overgeslagen.</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink-faint">Meerdere e-mailadressen scheiden met een komma.</p>
        </div>
      ) : (
        <label className="block">
          <span className="label-caps mb-1.5 block text-ink-faint">Ontvangers</span>
          <input
            value={form.globalText}
            onChange={(e) => setForm((current) => ({ ...current, globalText: e.target.value }))}
            className="field"
            placeholder="naam@klant.nl, tweede@klant.nl"
            aria-label="Ontvangers"
            readOnly={!canEdit}
          />
          <span className="mt-1 block text-xs text-ink-faint">Meerdere e-mailadressen scheiden met een komma.</span>
        </label>
      )}

      <div className="flex items-center justify-between border-t border-hairline pt-3">
        <div>
          <p className="text-sm font-medium text-ink">Wekelijkse verzending</p>
          <p className="text-xs text-ink-faint">
            {hasRecipients ? 'Elke maandag automatisch een mail per project over de afgelopen week.' : 'Voeg eerst een ontvanger toe.'}
          </p>
        </div>
        <Toggle
          checked={form.weeklyEnabled && hasRecipients}
          onChange={(weeklyEnabled) => setForm((current) => ({ ...current, weeklyEnabled }))}
          label="Wekelijkse verzending"
          disabled={!hasRecipients || !canEdit}
        />
      </div>

      {canEdit ? (
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-secondary h-8 px-3 text-sm"
            onClick={save}
            disabled={!dirty || updateSettings.isPending}
          >
            {updateSettings.isPending ? 'Opslaan…' : 'Ontvangers opslaan'}
          </button>
        </div>
      ) : null}
    </section>
  )
}
